import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Profile from "./Profile";
import Notifications from "./Notifications";
import AdHistory from "./AdHistory";
import Statistics from "./Statistics";
import CreateAd from "./CreateAd";
import MobileBottomNavbar from "./components/Navigation/MobileBottomNavbar";
import MobileTopNavbar from "./components/Navigation/MobileTopNavbar";
import DesktopSidebar from "./components/Navigation/DesktopSidebar";
import backgroundImage from "assets/images/backgroundDashboard.png";
import axios from "axios";
import ConnectedPlatforms from "./ConnectedPlatforms";
import AdsRuleSet from "./AdsRuleSet";
import AdsHistory from "./AdsHistory";
import CampaignManager from "./CampaignManager";
import Billing from "./Billing";
import { removeAuthToken } from "utils/auth";
import { getRequest, postRequest } from "services/api/apiService";
import CreateAdWithAI from "./CreateAdWithAI";
import AdAiLauncher from "./AdAiLauncher";
import AdLibraryPage from "./AdLibraryPage";
import CreateAdFromLibrary from "./CreateAdFromLibrary";
import OnboardingFlow from "./OnboardingFlow";

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState({ name: "", email: "", isPremium: false });
  const [unreadCount, setUnreadCount] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await getRequest("/api/auth/user-info");
        setUser(response);
        // Onboarding completion is persisted server-side (users.onboarding_done),
        // so it follows the user across devices/browsers — not just this localStorage.
        if (!response.onboarding_done) {
          setShowOnboarding(true);
        }
      } catch (error) {
        console.error("Failed to fetch user details:", error);
      }
    };

    const fetchUnreadCount = async () => {
      try {
        const res = await getRequest("/api/notifications/unread-count");
        setUnreadCount(res.count || 0);
      } catch {}
    };

    fetchUserDetails();
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);

    return () => clearInterval(interval);
  }, []);

  const handleDismissOnboarding = async () => {
    setShowOnboarding(false);
    setUser((u) => ({ ...u, onboarding_done: true }));
    try {
      await postRequest("/api/auth/complete-onboarding");
    } catch (error) {
      // Non-fatal: the overlay is dismissed for this session regardless;
      // it will reappear on next load if the write didn't land.
      console.error("Failed to save onboarding status:", error);
    }
  };

  const handleLogout = () => {
    removeAuthToken();
    navigate("/");
  };

  const activeStyle =
    "text-white bg-[#221C51]/90 rounded-lg shadow-md px-4 py-2";
  const inactiveStyle =
    "text-[#221C51] bg-transparent hover:bg-[#F6F6F6] hover:text-[#221C51] transition ease-in-out duration-200 rounded-lg px-4 py-2 transform hover:scale-105";

  return (
    <div
      className="min-h-screen h-screen bg-cover bg-center p-4 flex flex-col lg:flex-row overflow-hidden"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {/* Sidebar for Web */}
      <DesktopSidebar
        user={user}
        activeStyle={activeStyle}
        inactiveStyle={inactiveStyle}
        handleLogout={handleLogout}
        unreadCount={unreadCount}
        onOpenOnboarding={() => setShowOnboarding(true)}
      />

      {/* Navbar and User Section for Mobile */}
      <MobileTopNavbar />

      {/* Main Content */}
      <div className="flex-1 w-full bg-white rounded-xl pt-2 p-3 lg:p-6 shadow-md overflow-y-auto mb-16 mt-0 lg:mt-[-10px] lg:mb-0 lg:ml-6">
        <Routes>
          <Route index element={<Navigate to="statistics" replace />} />
          <Route path="create-ad" element={<CreateAd isPremium={user.isPremium} />} />
          <Route path="create-ad-ai" element={<CreateAdWithAI isPremium={user.isPremium} />} />
          <Route path="profile" element={<Profile />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="history" element={<AdHistory />} />
          <Route path="statistics" element={<Statistics />} />
          <Route path="connected-platforms" element={<ConnectedPlatforms />} />
          <Route path="ads-ruleset" element={<AdsRuleSet />} />
          <Route path="ads-history" element={<AdsHistory />} />
          <Route path="campaigns" element={<CampaignManager />} />
          <Route path="ai-launcher" element={<AdAiLauncher />} />
          <Route path="ad-library" element={<AdLibraryPage />} />
          <Route path="create-ad-from-library" element={<CreateAdFromLibrary />} />
          <Route path="billing" element={<Billing />} />
        </Routes>
      </div>

      {/* Bottom Navigation Bar for Mobile */}
      <MobileBottomNavbar
        user={user}
        handleLogout={handleLogout}
        unreadCount={unreadCount}
      />

      {/* Onboarding overlay — shown once on first visit */}
      {showOnboarding && <OnboardingFlow onDismiss={handleDismissOnboarding} />}
    </div>
  );
};

export default Dashboard;
